/**
 * useMapBuildings hook
 * Fetches buildings for a location and transforms them for 3D rendering
 * Supports template-aware rendering with units and doors
 */

import { useMemo } from 'react'
import { useHangarsWithUnitsFor3D } from './useHangars'
import { useLocation } from './useLocations'
import { BuildingLoaderService } from '@/services/3d'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

export interface ViewSettings {
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
}

interface UseMapBuildingsResult {
  /** Buildings transformed for 3D rendering (with template data) */
  buildings: Building3DConfigExtended[]
  /** Scene center coordinates [lng, lat] — undefined while location is loading */
  center: [number, number] | undefined
  /** View settings for Mapbox camera — undefined while location is loading */
  viewSettings: ViewSettings | undefined
  /** Loading state */
  isLoading: boolean
  /** Error state */
  error: Error | null
}

/**
 * Fetch buildings for a location and transform for 3D rendering
 * Uses template-aware data transformation for buildings with units
 *
 * Center and viewSettings are undefined while location data is loading.
 * Consumers must guard against undefined (e.g., viewSettingsReady flag)
 * to prevent rendering with wrong coordinates.
 *
 * @param locationId - Location ID to fetch buildings for
 * @returns Transformed buildings and view settings
 */
export function useMapBuildings(locationId: string | undefined): UseMapBuildingsResult {
  // Fetch location details (for center coordinates and view settings)
  const {
    data: location,
    isLoading: locationLoading,
    error: locationError,
  } = useLocation(locationId)

  // Fetch buildings WITH their units for template-aware 3D rendering
  const {
    data: hangarsWithUnits,
    isLoading: hangarsLoading,
    error: hangarsError,
  } = useHangarsWithUnitsFor3D(locationId)

  // Scene center — undefined until location data arrives (no Detroit fallback)
  const center = useMemo<[number, number] | undefined>(() => {
    if (location?.longitude && location?.latitude) {
      return [location.longitude, location.latitude]
    }
    return undefined
  }, [location?.longitude, location?.latitude])

  // View settings from location — undefined until location data arrives
  const viewSettings = useMemo<ViewSettings | undefined>(() => {
    if (location) {
      return BuildingLoaderService.getLocationViewSettings({
        latitude: location.latitude,
        longitude: location.longitude,
        settings: location.settings as Record<string, unknown> | undefined,
      })
    }
    return undefined
  }, [location])

  // Transform buildings with units for template-aware 3D rendering
  // Skipped when center is undefined (location not loaded yet)
  const buildings = useMemo<Building3DConfigExtended[]>(() => {
    if (!hangarsWithUnits || hangarsWithUnits.length === 0 || !center) {
      return []
    }
    return BuildingLoaderService.transformBuildingsWith3DData(hangarsWithUnits, center)
  }, [hangarsWithUnits, center])

  return {
    buildings,
    center,
    viewSettings,
    isLoading: locationLoading || hangarsLoading,
    error: locationError || hangarsError || null,
  }
}
